import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { DialogInputComponent, DialogInputData } from 'src/app/components/shared/dialog-input/dialog-input.component';
import { GroupBy } from '../_enums/groupby';
import { ResourceTypeCodes } from '../_enums/resource.type.codes';
import { SiteMapUrl } from '../_enums/sitemap.url';
import {
  CallForTender,
  CallForTenderCreationModel,
  CallForTenderLinkProjectPayload,
  CallForTenderList,
  CallForTenderProjectRankPayload,
  CallForTenderSelectIndicatorsPayload,
  CallForTenderSelectSolutionPayload,
  CallForTenderVersionPayload
} from '../_models/callForTender';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from './snackbar.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { SnackbarTypes } from '../_enums/snackbar.types';
import { Indicator } from '../_models/indicator';
import { GlobalConstants } from '../../../constants/global.constants';
import { VersionService } from './versions.service';

@Injectable({
  providedIn: 'root'
})
export class CallForTenderService {
  cftCreated$ = new Subject<CallForTender>();
  cftDeleted$ = new Subject<string[]>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private versionService: VersionService
  ) {}

  getAll(): Observable<CallForTenderList> {
    return this.http.get<CallForTenderList>(`${environment.apiUrl}/CallForTenders`, { headers: this.authService.headers });
  }

  get(id: string): Observable<CallForTender | null> {
    return this.http.get<CallForTender | null>(`${environment.apiUrl}/CallForTenders/${id}`, { headers: this.authService.headers });
  }

  openCreateModal(): void {
    this.versionService.getAll().subscribe((versions) => {
      const dialogData: DialogInputData = {
        title: this.translate.instant('cft_create_modal_title'),
        fields: [
          {
            key: 'displayName',
            label: this.translate.instant('cft_field_name_label'),
            placeholder: this.translate.instant('cft_field_name_placeholder'),
            required: true,
            maxLength: 50
          },
          {
            key: 'code',
            label: this.translate.instant('cft_field_code_label'),
            placeholder: this.translate.instant('cft_field_code_placeholder'),
            required: true,
            maxLength: 50
          },
          {
            key: 'versionId',
            label: this.translate.instant('cft_db_version_label'),
            placeholder: this.translate.instant('cft_field_version_placeholder'),
            type: 'select',
            required: true,
            options: (versions ?? []).filter((version) => version.versionId).map((version) => ({ value: version.versionId as string, label: version.versionName ?? '' }))
          }
        ]
      };

      const dialogRef = this.dialog.open(DialogInputComponent, {
        data: dialogData
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (!result || !result.displayName || !result.code || !result.versionId) return;
        this.create(result.displayName, result.code, result.versionId).subscribe((r) => {
          this.snackbarService.open(SnackbarTypes.add);
          if (!r) return;
          this.updateVersion(r.id, result.versionId).subscribe(() => {
            this.cftCreated$.next(r);
          });
        });
      });
    });
  }

  create(displayName: string, code: string, versionId: string): Observable<CallForTender | null> {
    const newCallForTender: CallForTenderCreationModel = {
      displayName,
      code,
      versionId
    };
    return this.http.post<CallForTender>(`${environment.apiUrl}/CallForTenders`, newCallForTender, { headers: this.authService.headers });
  }

  delete(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${environment.apiUrl}/CallForTenders/${id}`, { headers: this.authService.headers });
  }

  patchSelectedSolutionId(callForTenderId: string, rowId: string, solutionId: string): Observable<boolean> {
    const payload: CallForTenderSelectSolutionPayload = {
      cftId: callForTenderId,
      rowId: rowId,
      solutionId: solutionId
    };
    return this.http.put<boolean>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/${rowId}/selectedSolution`, payload, { headers: this.authService.headers });
  }

  updateRowRanks(callForTenderId: string, ranks: { rowId: string; rank: number }[]): Observable<boolean> {
    const payload: CallForTenderProjectRankPayload = {
      cftId: callForTenderId,
      rank: ranks.map((r) => r.rank),
      rowId: ranks.map((r) => r.rowId)
    };
    return this.http.put<boolean>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/updateRowRanks`, payload, { headers: this.authService.headers });
  }

  updateIndicators(callForTenderId: string, indicatorSelected: Indicator[]) {
    const payload: CallForTenderSelectIndicatorsPayload = {
      cftId: callForTenderId,
      indicatorIds: indicatorSelected.filter((i) => i.isChecked && i.indicatorId).map((indicator) => indicator.indicatorId ?? GlobalConstants.EMPTY_GUID)
    };
    return this.http.put<boolean>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/selectedIndicators`, payload, { headers: this.authService.headers });
  }

  updateVersion(callForTenderId: string, versionId: string): Observable<boolean> {
    const payload: CallForTenderVersionPayload = {
      cftId: callForTenderId,
      versionId: versionId
    };
    return this.http.put<boolean>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/updateVersion`, payload, { headers: this.authService.headers });
  }

  deleteRow(callForTenderId: string, rowId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/${rowId}`, { headers: this.authService.headers });
  }

  linkProject(callForTenderId: string, payload: CallForTenderLinkProjectPayload): Observable<string> {
    return this.http.post<string>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/linkProjectToCallForTenders`, payload, { headers: this.authService.headers });
  }

  getSynthesis(callForTenderId: string, indicatorIds: string[]): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/CallForTenders/${callForTenderId}/synthesis`, { indicatorIds }, { headers: this.authService.headers });
  }

  getNavConfig({ perimeterId }: NavListCommonProps) {
    return {
      header: {
        title: this.translate.instant('cft_nav_title'),
        actions: [{ label: this.translate.instant('cft_btn_create_tender'), onClick: () => this.openCreateModal() }],
        filters: [],
        defaultFilter: GroupBy.family
      },
      getItemConfig: (node: any) => {
        return {
          displayName: `${node.displayName} [${node.code}]`,
          url: `${SiteMapUrl.MenuCallForTender}/${node.id}`,
          showBadge: false,
          state: {
            resourceId: node.id,
            perimeterId
          }
        };
      },
      getDataSource: () => {
        return this.getAll().pipe(map((callForTenderList: CallForTenderList) => callForTenderList.rows));
      },
      getEditableFamilyId: (node: any) => node.value?.[0].familyId,
      addFamilyType: ResourceTypeCodes.Produit
    };
  }
}
